import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

// Pages
import AuthPage from "@/pages/AuthPage";
import AgentDashboard from "@/pages/AgentDashboard";
import CustomerPortal from "@/pages/CustomerPortal";
import KnowledgeBase from "@/pages/KnowledgeBase";

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === 'customer') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Default route - redirect to login or dashboard */}
      <Route path="/" element={
        user ? (
          <Navigate to={user.role === 'customer' ? '/portal' : '/dashboard'} replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      {/* Login Page */}
      <Route path="/login" element={
        user ? (
          <Navigate to={user.role === 'customer' ? '/portal' : '/dashboard'} replace />
        ) : (
          <AuthPage />
        )
      } />
      
      {/* Knowledge Base - accessible to all */}
      <Route path="/knowledge" element={<KnowledgeBase />} />
      <Route path="/knowledge/:articleId" element={<KnowledgeBase />} />

      {/* Agent/Admin Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['agent', 'admin']}>
            <AgentDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/*" 
        element={
          <ProtectedRoute allowedRoles={['agent', 'admin']}>
            <AgentDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Customer Routes */}
      <Route 
        path="/portal" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerPortal />
          </ProtectedRoute>
        } 
      />

      {/* Fallback - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
